// OWNER: FRONTEND-DIALER agent. The main screen (SPEC §15.1): campaign picker, lead card, script, call controls,
// disposition bar, Book panel, keyboard-first. Session state lives in web/src/dialer/store.ts so it survives navigation.
import '../dialer/dialer.css';
import { useEffect, useRef, useState } from 'preact/hooks';
import { Modal } from '../components/ui';
import { Spinner, useExternal, useNow } from '../components/ui-extra';
import { navigate } from '../app';
import { useMe } from '../me';
import { Keypad } from '../phone/Keypad';
import { usePhone } from '../phone/PhoneProvider';
import { PhoneStatusPill } from '../phone/StatusPill';
import { BookPanel } from '../dialer/BookPanel';
import { dispensaryBooking } from '../dialer/book';
import { EmailPanel } from '../dialer/EmailPanel';
import { TextPanel } from '../dialer/TextPanel';
import { CallBar } from '../dialer/CallBar';
import { dialerKeyAction, HOTKEY_HELP, type HotkeyAction } from '../dialer/hotkeys';
import { BusinessDrawer } from '../dialer/drawer/BusinessDrawer';
import { blockedSentences, DISPOSITION_META, HAND_PICK_STEPS, handPickHref, handPickLine } from '../dialer/labels';
import { LeadCard, StateNote } from '../dialer/LeadCard';
import { isLive } from '../dialer/machine';
import { Picker } from '../dialer/Picker';
import { calendlyLink, firstName, scriptVars } from '../dialer/script';
import { Filled, LiveVoicemailCard, ScriptPanel } from '../dialer/ScriptPanel';
import {
  callBackNow, callOtherLine, checkPending, chooseDisposition, chooseOpener, clearSkipsAndReload, closeDrawer, dial, dismissBlock, drawerProps, dropVoicemail, getDialer, hangup,
  loadNext, markLiveVoicemail, openMembership, recheckLive, refreshPace, refreshToday, researchBusiness, saveAndDialNext, saveDisposition, sendDigits,
  setDialerVisible, skip, stopDialing, subscribeDialer, toggleDrawer, toggleMute, updateWrap, verifyHook, type DialerState,
} from '../dialer/store';
import { paceItems } from '../dialer/pace';
import { fmtDateTime } from '../components/ui';
import { fmtPhone } from '../dialer/labels';
import { WrapUpDock } from '../dialer/WrapUpDock';
import { usePresenceBeat } from '../tracking/presence'; // setter tracking (SPEC §19, 2026-10-01)

type Panel = 'none' | 'book' | 'livevm' | 'email' | 'text';

/** Migration 0006: leads the queue never serves (Oklahoma), with the way to open them by hand. One plain line. */
function HandPickNote(props: { campaignId: number | null; handPick: DialerState['handPick'] }) {
  const hp = props.handPick;
  const line = hp ? handPickLine(hp.total, hp.states) : null;
  if (!hp || !line) return null;
  const href = handPickHref(props.campaignId, hp.states);
  return (
    <p class="vd-handpick" role="note">
      {line}{' '}
      <a href={href} title="The dialer never picks these for you; open each one yourself" onClick={(e) => { e.preventDefault(); navigate(href); }}>{HAND_PICK_STEPS}</a>
    </p>
  );
}

/** Live pace (REVIEW-2026-10-01 §4.4): dials last hour / today, connects, talk, booked, and time since the last dial.
 *  Its own 5-second clock, so only this line re-renders as "since last dial" ticks. */
function PaceStats(props: { s: DialerState }) {
  const now = useNow(5000);
  const { s } = props;
  const items = paceItems(s.pace, now, { localLastDialAt: s.lastDialAt, onCall: isLive(s.call), booked: s.today?.mine?.meetingsBooked ?? null });
  if (!items.length) {
    const mine = s.today?.mine;
    return mine ? <span class="muted vd-topbar-stats">Today: {mine.dials} dials · {mine.connects} connects · {mine.meetingsBooked} booked</span> : null;
  }
  return (
    <span class="vd-pace" aria-label="Your pace">
      {items.map((x) => <span class={x.warn ? 'vd-pace-idle' : ''} title={x.title}>{x.label} <b>{x.value}</b></span>)}
    </span>
  );
}

/** True while the window matches `query` (the drawer becomes a third column at 1600 px and wider). */
function useMedia(query: string): boolean {
  const get = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(query).matches;
  const [on, setOn] = useState(get);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia(query);
    const onChange = () => setOn(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [query]);
  return on;
}

export function Dialer(_props: { params: Record<string, string> }) {
  const me = useMe();
  const s = useExternal(subscribeDialer, getDialer);
  usePresenceBeat();
  const p = usePhone();
  const [panel, setPanel] = useState<Panel>('none');
  const [keypad, setKeypad] = useState(false);
  const [objection, setObjection] = useState<number | null>(null);
  const [chord, setChord] = useState<'objection' | null>(null);
  const [help, setHelp] = useState(false);
  const [dtmf, setDtmf] = useState('');
  const sendDtmf = (d: string) => { sendDigits(d); setDtmf((x) => (x + d).slice(-24)); };
  const wide = useMedia('(min-width: 1600px)');

  useEffect(() => {
    setDialerVisible(true);
    if (s.pending !== 'clear') void checkPending();
    if (!getDialer().today) void refreshToday();
    else void refreshPace();
    const paceTimer = setInterval(() => { void refreshPace(); }, 60_000);
    const open = Number(new URLSearchParams(location.search).get('open'));
    if (Number.isInteger(open) && open > 0) {
      history.replaceState(null, '', '/');
      void openMembership(open);
    }
    return () => { clearInterval(paceTimer); setDialerVisible(false); };
  }, []);

  // Per-lead UI resets when a different lead comes on screen.
  // A no-campaign item (SPEC §19, 2026-10-02) has membership id 0: key it by business instead.
  const leadId = s.item ? (s.item.noCampaign ? -s.item.business.id : s.item.membership.id) : null;
  useEffect(() => { setPanel('none'); setKeypad(false); setObjection(null); setChord(null); }, [leadId]);
  useEffect(() => { if (!isLive(s.call)) { setKeypad(false); setDtmf(''); } }, [s.call.phase]);
  useEffect(() => {
    if (!chord) return;
    const t = setTimeout(() => setChord(null), 2500);
    return () => clearTimeout(t);
  }, [chord]);

  // No campaign on the lead on screen (SPEC §19, 2026-10-02): no script, objections or live voicemail. Booking works
  // since PJ 2026-10-20 (the booking they made on the Calendly link, or one booked elsewhere: BookPanel).
  const noCampaign = !!s.item?.noCampaign;
  const campaign = noCampaign ? null : s.campaign;
  const objections = campaign?.script?.objections ?? [];
  const selectObjection = (i: number | null) => {
    setObjection(i);
    const card = i !== null ? objections[i] : undefined;
    if (card && !getDialer().wrap.objection) updateWrap({ objection: card.category });
  };
  const toggleLiveVm = () => {
    if (panel === 'livevm') { setPanel('none'); return; }
    setPanel('livevm');
    if (getDialer().call.phase === 'in_call') markLiveVoicemail();
  };

  // Keyboard: one listener, reading the latest state through a ref.
  const handler = useRef<(e: KeyboardEvent) => void>(() => {});
  handler.current = (e: KeyboardEvent) => {
    if (e.defaultPrevented || s.view !== 'lead') return;
    const target = e.target as HTMLElement | null;
    if (e.key !== 'Escape' && document.querySelector('[role="dialog"], [role="alertdialog"]')) return; // a modal owns the keys
    if (e.key === ' ' && target?.tagName === 'INPUT') return; // Space toggles a focused checkbox, never dials
    // Quiet while typing in a box. The Business drawer is not a dialog: every key keeps working while it is open.
    const action = dialerKeyAction(e, target, { phase: s.call.phase, chord, keypadOpen: keypad, drawerOpen: s.drawer.open });
    if (!action) { if (chord) setChord(null); return; }
    e.preventDefault();
    run(action);
  };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => handler.current(e);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  function run(a: HotkeyAction): void {
    if (a.type !== 'objectionChord' && a.type !== 'objection') setChord(null);
    switch (a.type) {
      case 'dial': void dial(); break;
      case 'hangup': hangup(); break;
      case 'mute': toggleMute(); break;
      case 'keypad': setKeypad((k) => !k); break;
      case 'dtmf': sendDtmf(a.digit); break;
      case 'drop': void dropVoicemail(me.id); break;
      case 'liveVm': if (s.item && !noCampaign) toggleLiveVm(); break;
      case 'book': if (s.item) setPanel(panel === 'book' ? 'none' : 'book'); break;
      case 'text': if (s.item && s.item.business.id > 0) setPanel(panel === 'text' ? 'none' : 'text'); break;
      case 'skip': void skip(); break;
      case 'callBack': void callBackNow(); break;
      case 'otherLine': void callOtherLine(); break;
      case 'objectionChord': if (objections.length) setChord('objection'); break;
      case 'objection': setChord(null); if (a.index < objections.length) selectObjection(objection === a.index ? null : a.index); break;
      case 'dispose': chooseDisposition(a.code); break;
      case 'save': void saveDisposition(); break;
      case 'saveDial': void saveAndDialNext(); break;
      case 'help': setHelp(true); break;
      case 'drawer': if (s.item) toggleDrawer(); break;
      case 'recheck': recheckLive(); break;
      case 'escape':
        if (keypad) setKeypad(false);
        else if (s.drawer.open && objection !== null) setObjection(null); // the objection card sits above the drawer
        else if (s.drawer.open) closeDrawer();
        else if (panel !== 'none') setPanel('none');
        else if (objection !== null) setObjection(null);
        else if (s.call.phase === 'blocked') dismissBlock();
        break;
    }
  }

  if (s.view === 'picker') {
    return (
      <div class="vd-dialer-page">
        <div class="vd-topbar"><span /><PhoneStatusPill /></div>
        <Picker />
      </div>
    );
  }

  const item = s.item;
  const setter = firstName(me.name);
  const vars = item ? scriptVars({
    setterName: me.name, business: item.business, dmName: s.wrap.dmName || null,
    callback: s.call.callerId ? fmtPhone(s.call.callerId) : null,
    // The email and text booking links follow the shared rule: the campaign's link, /your-store for a cannabis business.
    calendlyLink: calendlyLink(campaign?.calendlyEventSlug ?? null, setter, 'email', dispensaryBooking([item.business.niche, campaign?.niche])),
    mailingAddress: s.settings?.mailingAddress ?? null,
  }) : {};
  const canLeave = s.call.phase === 'idle' || s.call.phase === 'blocked';

  return (
    <div class="vd-dialer">
      <div class="vd-topbar">
        <div class="row" style="gap:10px;min-width:0">
          <button class="btn btn-sm" disabled={!canLeave} title={canLeave ? 'Back to campaigns' : 'Finish the call first'} onClick={stopDialing}>← Campaigns</button>
          <strong class="vd-topbar-campaign" title={noCampaign ? 'A dial-pad call with no campaign' : s.campaign?.name}>{noCampaign ? 'No campaign' : s.campaign?.name ?? item?.campaign.name ?? 'Campaign'}</strong>
          <PaceStats s={s} />
          {s.lastSaved && Date.now() - s.lastSaved.at < 8000 ? <span class="chip chip-ok">Saved: {DISPOSITION_META[s.lastSaved.code].label}{s.lastSaved.business ? `, ${s.lastSaved.business}` : ''}</span> : null}
        </div>
        <div class="row" style="gap:8px">
          <PhoneStatusPill />
          <button class="btn btn-sm" onClick={() => setHelp(true)} title="Keyboard shortcuts">?</button>
        </div>
      </div>

      {item ? <CallBar s={s} phoneState={p} userId={me.id} keypadOpen={keypad} onKeypad={() => setKeypad(!keypad)} /> : null}
      {keypad && isLive(s.call) ? (
        <div class="vd-keypad-float card"><Keypad onDigit={sendDtmf} sent={dtmf} extension={item?.business.phoneExt} onClose={() => setKeypad(false)} /></div>
      ) : null}

      {/* REVIEW-2026-10-01: the hand-pick (Oklahoma) line is noise on the call screen; it shows only when the queue is empty. */}
      {s.call.phase === 'blocked' && s.call.gate ? (
        <div class="vd-blocked card" role="alert">
          <b>This call can't be placed right now.</b>
          <ul>{s.call.gate.reasons.map((r) => <li>{r.message}</li>)}</ul>
          {s.call.gate.retryAt ? <div class="muted">Earliest it could work: {fmtDateTime(s.call.gate.retryAt)} (your time).</div> : null}
          <div class="row" style="gap:8px;margin-top:8px">
            <button class="btn btn-primary" onClick={() => void skip()}>{noCampaign ? 'Close this number' : 'Skip to the next lead'} <kbd>S</kbd></button>
            <button class="btn" onClick={dismissBlock}>Back <kbd>Esc</kbd></button>
          </div>
        </div>
      ) : null}

      {s.lead === 'loading' && !item ? <div class="vd-center muted"><Spinner /> Finding the next lead…</div> : null}
      {s.lead === 'error' ? (
        <div class="vd-center">
          <p class="vd-bad">{s.leadError}</p>
          <button class="btn" onClick={() => void loadNext()}>Try again</button>
        </div>
      ) : null}
      {s.lead === 'empty' && s.empty ? (
        <div class="vd-center card vd-empty">
          <h2>No lead can be called right now</h2>
          {blockedSentences(s.empty.blockedCounts).length ? (
            <ul>{blockedSentences(s.empty.blockedCounts).map((t) => <li>{t}</li>)}</ul>
          ) : <p class="muted">Every lead in this campaign has been worked, is waiting for its next attempt window, or is closed.</p>}
          {s.empty.waitingCount ? <p class="muted" style="margin:4px 0">{s.empty.waitingCount} lead{s.empty.waitingCount === 1 ? ' is' : 's are'} resting until their next attempt or callback time.</p> : null}
          {s.empty.nextRetryLabel ? <p style="margin:4px 0"><b>Next lead opens {s.empty.nextRetryLabel}.</b></p> : null}
          <HandPickNote campaignId={s.campaignId} handPick={s.handPick} />
          {s.skipIds.length ? <p class="muted">{s.skipIds.length} lead{s.skipIds.length === 1 ? '' : 's'} you skipped this session {s.skipIds.length === 1 ? 'is' : 'are'} left out.</p> : null}
          <div class="row" style="gap:8px;justify-content:center">
            <button class="btn btn-primary" onClick={() => void loadNext()}>Check again</button>
            {s.skipIds.length ? <button class="btn" onClick={() => void clearSkipsAndReload()}>Include skipped leads</button> : null}
            <button class="btn" onClick={stopDialing}>Pick another campaign</button>
          </div>
        </div>
      ) : null}
      {s.leadError && s.lead === 'ready' ? <p class="vd-bad" style="margin:4px 0">{s.leadError}</p> : null}

      {item ? <StateNote item={item} loading={s.lead === 'loading'} /> : null}
      {item ? (
        <div class={`vd-body ${s.lead === 'loading' ? 'vd-loading' : ''} ${s.drawer.open && wide ? 'vd-body-pinned' : ''}`}>
          <div class="vd-col vd-col-lead">
            <LeadCard item={item} campaign={campaign} wrap={s.wrap} editable={true} />
          </div>
          <div class="vd-col vd-col-script">
            <div class="vd-col-head">
              <span class="eyebrow">{panel === 'book' ? 'Book a meeting' : panel === 'email' ? 'Send email' : panel === 'text' ? 'Text' : 'Script'}</span>
              <div class="row">
                <button class={`btn btn-sm ${panel === 'livevm' ? 'btn-ink' : ''}`} disabled={noCampaign} title={noCampaign ? 'Live voicemail scripts belong to a campaign' : undefined}
                  onClick={(e) => { (e.currentTarget as HTMLElement).blur(); toggleLiveVm(); }}>Live VM <kbd>L</kbd></button>
                <button class={`btn btn-sm ${panel === 'book' ? 'btn-ink' : ''}`} title={noCampaign ? 'Link the booking they made on the Calendly link, or log one booked elsewhere' : undefined}
                  onClick={(e) => { (e.currentTarget as HTMLElement).blur(); setPanel(panel === 'book' ? 'none' : 'book'); }}>Book <kbd>B</kbd></button>
              </div>
            </div>
            <div class="vd-col-body">
              {panel === 'email' ? (
                <EmailPanel item={item} callId={s.call.callId} wrap={s.wrap} onClose={() => setPanel('none')}
                  canSetOutcome={s.call.phase === 'needs_disposition' || s.call.phase === 'in_call'} />
              ) : panel === 'text' ? (
                <TextPanel item={item} calendlySlug={campaign?.calendlyEventSlug ?? null} onClose={() => setPanel('none')} />
              ) : panel === 'book' ? (
                <BookPanel item={item} campaignSlug={campaign?.calendlyEventSlug ?? null} callId={s.call.callId} wrap={s.wrap} onClose={() => setPanel('none')} />
              ) : noCampaign ? (
                <p class="muted">No campaign on this call, so there is no script or live voicemail. Say who you are and why
                  you're calling; every calling rule still applied before it rang. Did they book on a Calendly link you sent?
                  Press B to link it. After the call, save it to My leads or not.</p>
              ) : (
                <>
                  {panel === 'livevm' && s.campaign ? <LiveVoicemailCard campaign={s.campaign} item={item} vars={vars} onClose={() => setPanel('none')} /> : null}
                  <ScriptPanel campaign={s.campaign} campaignError={s.campaignError} item={item} vars={vars}
                    objection={objection} onObjection={selectObjection} chordActive={chord === 'objection'}
                    drawer={s.drawer.businessId === item.business.id ? { data: s.drawer.data, live: s.drawer.live } : null}
                    openerLabel={s.openerPick && s.openerPick.membershipId === item.membership.id ? s.openerPick.label : null}
                    onOpener={chooseOpener} openerLocked={s.call.phase !== 'idle' && s.call.phase !== 'blocked'} />
                </>
              )}
            </div>
          </div>
          {s.drawer.open ? (
            <BusinessDrawer {...drawerProps(s)} readOnly={false} pinned={wide} onClose={closeDrawer} onRecheck={recheckLive}
              onVerify={(h) => void verifyHook(h)} onResearch={() => void researchBusiness()} />
          ) : null}
          {s.drawer.open && !wide && objection !== null && objections[objection] ? (
            <div class="vd-objection-float card" role="note" aria-label="Objection answer">
              <div class="row" style="justify-content:space-between">
                <span class="eyebrow">{objections[objection].label}</span>
                <button class="btn btn-sm" onClick={() => setObjection(null)}>Close <kbd>Esc</kbd></button>
              </div>
              <p class="vd-script-text"><Filled text={objections[objection].response} vars={vars} /></p>
              {objections[objection].followUpQuestion ? <p class="vd-script-text"><b>Then:</b> <Filled text={objections[objection].followUpQuestion!} vars={vars} /></p> : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {item ? <WrapUpDock s={s} campaign={campaign} vars={vars} onBook={() => setPanel('book')} onEmail={() => setPanel('email')}
        onText={() => setPanel(panel === 'text' ? 'none' : 'text')} /> : null}

      {help ? (
        <Modal title="Keyboard shortcuts" onClose={() => setHelp(false)} width={520}>
          <table>
            <tbody>{HOTKEY_HELP.map(([k, v]) => <tr><td style="white-space:nowrap"><kbd>{k}</kbd></td><td>{v}</td></tr>)}</tbody>
          </table>
          <p class="muted" style="margin-bottom:0">Shortcuts pause while you type in a box. Click anywhere outside the box to use them again.</p>
        </Modal>
      ) : null}
    </div>
  );
}
